import axios from "axios";
import Swal from "sweetalert2";
import { flujoEnum, FlujoStatus } from "../types/enumTypes";
import dayjs from "dayjs";
import { Dispatch, SetStateAction } from "react";

export const obtenerDatosPE = async (
    anio: number,
    mes: number,
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void
) => {
    setLoading(true);
    setError(null);
    const requestData = { anio, mes, tipo, origen };
    try {
        const response = await axios.post(process.env.REACT_APP_APPLICATION_DISTRIBUCIONES + "/GenerarReporteFondos", requestData,
            {
                headers: {

                    "Content-Type": "application/json",
                    "x-api-key": process.env.REACT_APP_API_KEY || "",
                },
            }
        );
        const formatTwoDecimals = (num: number): string => {
            return num.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        };
        const toNumber = (value: string | undefined): number => {
            if (!value) return 0;
            return parseFloat(value.replace(/,/g, "")) || 0;
        };
        // Obtener las columnas numéricas dinámicamente
        const obtenerColumnasNumericas = (data: Record<string, string>[]): string[] => {
            if (data.length === 0) return [];
            const excludeFields = ['ClaveEstado', 'OrdenSFTGNL']; // Excluir campos no numéricos
            return Object.keys(data[0]).filter((key) => {
                if (excludeFields.includes(key)) return false;
                const value = data[0][key];
                return typeof value === "string" && !isNaN(parseFloat(value.replace(/,/g, "")));
            });
        };
        const columnasNumericas = obtenerColumnasNumericas(response.data);
        // Agregar la suma de cada fila manteniendo decimales
        const datosConTotales = response.data.map((fila: Record<string, string>) => {
            const totalFila = columnasNumericas.reduce((suma: number, columna: string) => {
                return suma + toNumber(fila[columna]);
            }, 0);
            return {
                ...fila,
                TOTALES: formatTwoDecimals(totalFila)
            };
        });
        // Crear una fila final con los totales de cada columna
        const filaTotales: Record<string, string | number> = { Municipio: "Totales" };
        columnasNumericas.forEach((columna) => {
            const sumaColumna = datosConTotales.reduce((suma: number, fila: Record<string, string | number>) => {
                return suma + toNumber(fila[columna] as string);
            }, 0);
            filaTotales[columna] = formatTwoDecimals(sumaColumna);
        });
        // Sumar la columna `TOTALES` y asignarlo en la fila de totales
        const totalGenerales = datosConTotales.reduce((suma: number, fila: Record<string, string | number>) => {
            return suma + (typeof fila["TOTALES"] === "number" ? fila["TOTALES"] : toNumber(fila["TOTALES"] as string));
        }, 0);
        filaTotales["TOTALES"] = formatTwoDecimals(totalGenerales);
        // Formatear todos los números en la fila de totales para mostrar 2 decimales
        Object.keys(filaTotales).forEach(key => {
            if (key !== "Municipio") {
                filaTotales[key] = (filaTotales[key] as number).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            }
        });
        // Agregar la fila de totales al final
        datosConTotales.push(filaTotales);
        setResponseData(datosConTotales);
    } catch (error) {
        console.error("Error al obtener el reporte:", error);
        setError("Hubo un error al obtener el reporte.");
    } finally {
        setLoading(false);
    }
};

export const guardarDatosMontosPE = async (
    titulo: string,
    fecha: any, // Puedes cambiar a Dayjs si usas Day.js
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    responseData: any[],
    setResponseData: (data: any[]) => void, // 👈 Agregado aquí
    setLoading: (loading: boolean) => void,
    setError: (error: string | null) => void,
    navigate: (path: string) => void // Recibe navigate como argumento
) => {
    setError(null);
    const errores = [];
    if (!fecha) {
        errores.push("La fecha es obligatoria.");
    }
    if (!titulo.trim()) {
        errores.push("El título es obligatorio.");
    }
    if (!responseData || responseData.length === 0) {
        errores.push("No hay datos en el reporte para guardar.");
    }
    if (errores.length > 0) {
        Swal.fire({
            icon: "error",
            title: "Error al guardar",
            html: `<ul style="text-align: left;">${errores.map(e => `<li>${e}</li>`).join("")}</ul>`,
            confirmButtonText: "Entendido",
            confirmButtonColor: 'black',
        });
        return;
    }
    const confirmacion = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Se guardarán los datos en el sistema.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, guardar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
    });
    if (!confirmacion.isConfirmed) {
        return;
    }
    const datosProcesados = responseData
        .filter(fila => !("Municipio" in fila) || fila.Municipio !== "Totales") // Eliminar la fila donde Municipio existe o es "Totales"
        .map(({ Municipio, Totales, OrdenSFTGNL, ...resto }) => resto); // Eliminar las claves "Municipio", "TOTALES" y "OrdenSFTGNL"
    const datosParaGuardar = {
        titulo, // Se asigna directamente en lugar de info.titulo
        fecha: dayjs(fecha).format("YYYY-MM-DD"), // Se formatea la fecha correctamente
        data: datosProcesados, // Datos sin "Municipio", "Totales" ni "OrdenSFTGNL"
    };
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/alta`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );

        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Guardado exitoso",
            text: "Los datos se han guardado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        navigate("/admin/participaciones/estatales/montos");
    } catch (error: any) {
        console.error("Error al guardar los datos:", error);
        // Obtener el mensaje de error del backend
        const mensajeError = error.response?.data?.message || "Hubo un error al guardar los datos.";
        setError(mensajeError);
        // Mostrar el mensaje exacto del backend en la alerta
        Swal.fire({
            title: "Error",
            text: mensajeError, // Mostrar mensaje recibido del backend
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    }
};

export const mostrarDatosListaMontosPE = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void
) => {
    setLoading(true);
    setError(null);
    try {
        const url = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/lista`;
        const { data } = await axios.get(url, {
            headers: {
                Authorization: localStorage.getItem("jwtToken") || "",
                "Content-Type": "application/json",
            },
        });
        setResponseData(data.data);
    } catch (error: any) {
        console.error("Error al obtener los datos:", error);
        setError("Hubo un error al obtener los datos.");
    } finally {
        setLoading(false);
    }
};

export const mostrarDatosDetallePE = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void,
    setTitulo: (titulo: string) => void,  // <-- Agregar setTitulo aquí
    setEstatus: Dispatch<SetStateAction<flujoEnum>>,
    idRegistro: string
) => {
    setLoading(true);
    setError(null);
    try {
        const url = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/detalle/${idRegistro}`; // Corrección en la concatenación de la URL
        const response = await axios.get(url, {
            headers: {
                // Authorization: `Bearer ${token}`, 
                Authorization: localStorage.getItem("jwtToken") || "",
                "Content-Type": "application/json", // Si es necesario
            },
        });
        let datosConTotales = response.data.data || [];
        if (datosConTotales.length > 0) {
            const columnasNumericas = Object.keys(datosConTotales[0]).filter((key) => key !== "municipio");
            const toNumber = (valor: string | number): number => {
                return typeof valor === "number" ? valor : parseFloat(valor) || 0;
            };
            const formatTwoDecimals = (num: number): string => {
                return num.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                });
            };
            // Crear la fila de totales con el nombre "Total"
            const filaTotal: Record<string, string | number> = { municipio: "TOTALES" };
            columnasNumericas.forEach((columna) => {
                const suma = datosConTotales.reduce((suma: number, fila: Record<string, string | number>) => {
                    return suma + toNumber(fila[columna] as string);
                }, 0);
                // Formatear el número con 2 decimales
                filaTotal[columna] = formatTwoDecimals(suma);
            });
            // Agregar la fila de "Total" después del último municipio
            datosConTotales.push(filaTotal);
        }
        setResponseData(datosConTotales);
        setTitulo(response.data.info.titulo);
        setEstatus(response.data.info.estatus);
    } catch (error: any) {
        console.error("Error al obtener los datos:", error);
        setError("Hubo un error al obtener los datos.");
    } finally {
        setLoading(false);
    }
};


export const FlujoMontosPE = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void,
    estatus: string,
    idRegistro: string,
    accion: "avanzar" | "devolver"
) => {
    const confirmacion = await Swal.fire({
        title: "¿Deseas actualizar el estatus?",
        text: "Esta acción actualizará el estatus del registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
        cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
        return; // El usuario canceló la acción
    }
    const siguienteEstatus = FlujoStatus(estatus, accion);
    const datosParaGuardar = { estatus: siguienteEstatus };
    setLoading(true);
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/actualiza/${idRegistro}/`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El estatus se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        window.location.reload(); // Recargar la página después de la confirmación
    } catch (error) {
        console.error("Error al actualizar los datos:", error);
        setError("Hubo un error al actualizar los datos.");
        // Mostrar SweetAlert de error
        Swal.fire({
            title: "Error",
            text: "No se pudo actualizar el estatus. Inténtalo de nuevo.",
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    } finally {
        setLoading(false);
    }
};

export const EditarTituloPE = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    cancelarRegistro: Function,
    setError: (error: string | null) => void,
    idRegistro: string,
    titulo: string
) => {
    // Mostrar SweetAlert de confirmación
    const confirmacion = await Swal.fire({
        title: "¿Deseas guardar el nuevo título?",
        text: "Esta acción actualizará el título del registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
        cancelButtonText: "Cancelar",
    });
    if (!confirmacion.isConfirmed) {
        cancelarRegistro();
        return; // El usuario canceló la acción
    }
    const datosParaGuardar = { titulo };

    setLoading(true);
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/edita/${idRegistro}/`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El estatus se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        window.location.reload();
    } catch (error) {
        console.error("Error al actualizar los datos:", error);
        setError("Hubo un error al actualizar los datos.");
        // Mostrar SweetAlert de error
        Swal.fire({
            title: "Error",
            text: "No se pudo actualizar el estatus. Inténtalo de nuevo.",
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    } finally {
        setLoading(false);
    }
};

export const FlujoMemoriasPE = async (
    tipo: "participaciones" | "aportaciones",
    origen: "federales" | "estatales",
    setLoading: (loading: boolean) => void,
    setResponseData: (data: any[]) => void,
    setError: (error: string | null) => void,
    estatus: string,
    idRegistro: string,
    accion: "avanzar" | "devolver"
) => {
    const confirmacion = await Swal.fire({
        title: "¿Deseas actualizar el estatus?",
        text: "Esta acción actualizará el estatus del registro.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, actualizar",
        confirmButtonColor: "#15212f",
        cancelButtonColor: "#bda889",
        cancelButtonText: "Cancelar",
    });

    if (!confirmacion.isConfirmed) {
        return; // El usuario canceló la acción
    }

    const siguienteEstatus = FlujoStatus(estatus, accion);
    const datosParaGuardar = { estatus: siguienteEstatus };
    setLoading(true);
    try {
        const response = await axios.put(
            `${process.env.REACT_APP_APPLICATION_ENDPOINT}/${tipo}/${origen}/memorias/actualiza/${idRegistro}/`,
            datosParaGuardar,
            {
                headers: {
                    Authorization: localStorage.getItem("jwtToken") || "",
                    "Content-Type": "application/json", // Si es necesario
                },
            }
        );
        setResponseData(response.data);
        // Mostrar SweetAlert de éxito y recargar la página
        await Swal.fire({
            title: "Actualización exitosa",
            text: "El estatus se ha actualizado correctamente.",
            icon: "success",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
        window.location.reload(); // Recargar la página después de la confirmación
    } catch (error) {
        console.error("Error al actualizar los datos:", error);
        setError("Hubo un error al actualizar los datos.");
        // Mostrar SweetAlert de error
        Swal.fire({
            title: "Error",
            text: "No se pudo actualizar el estatus. Inténtalo de nuevo.",
            icon: "error",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#15212f",
        });
    } finally {
        setLoading(false);
    }
};